import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authClient } from '../lib/auth-client';
import { API_BASE_URL } from '../lib/api-config';

type AuthMode = 'signin' | 'signup' | 'forgot-password' | 'check-email';

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

function LogoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 28 28" fill="none">
      <rect width="28" height="28" rx="7" fill="#0072FF" />
      <path d="M7.5 14h6m0 0l5.5-5m-5.5 5l5.5 5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function GitHubIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path fill="currentColor" d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
    </svg>
  );
}

function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 21 21">
      <rect fill="#f25022" x="1" y="1" width="9" height="9" />
      <rect fill="#7fba00" x="11" y="1" width="9" height="9" />
      <rect fill="#00a4ef" x="1" y="11" width="9" height="9" />
      <rect fill="#ffb900" x="11" y="11" width="9" height="9" />
    </svg>
  );
}

function LastUsedBadge() {
  return (
    <span className="absolute -top-2 -right-2 rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-medium text-primary-600">
      Last used
    </span>
  );
}

export function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Derive initial mode from URL path
  const initialMode: AuthMode = location.pathname === '/sign-up' ? 'signup' : 'signin';
  const [mode, setMode] = useState<AuthMode>(initialMode);

  // Read plan param from URL
  const params = new URLSearchParams(location.search);
  const planParam = params.get('plan') as 'pro' | 'business' | null;
  const validPlan = planParam === 'pro' || planParam === 'business' ? planParam : null;

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [linkedProviders, setLinkedProviders] = useState<string[] | null>(null);
  const [lastMethod, setLastMethod] = useState<string | null>(null);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isGitHubLoading, setIsGitHubLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);

  const anyLoading = isLoading || isGoogleLoading || isGitHubLoading || isMicrosoftLoading;

  useEffect(() => {
    const method = authClient.getLastUsedLoginMethod?.();
    if (method) setLastMethod(method);
  }, []);

  // Sync URL when mode changes (preserve plan param)
  const switchMode = (newMode: AuthMode) => {
    setError(null);
    setSuccessMessage(null);
    setLinkedProviders(null);
    setMode(newMode);
    const planSuffix = validPlan ? `?plan=${validPlan}` : '';
    if (newMode === 'signin') navigate(`/sign-in${planSuffix}`, { replace: true });
    else if (newMode === 'signup') navigate(`/sign-up${planSuffix}`, { replace: true });
  };

  const providerNames: Record<string, string> = {
    google: 'Google',
    github: 'GitHub',
    microsoft: 'Microsoft',
  };

  const handleSocialSignIn = async (provider: 'google' | 'github' | 'microsoft') => {
    setError(null);
    const setLoading = {
      google: setIsGoogleLoading,
      github: setIsGitHubLoading,
      microsoft: setIsMicrosoftLoading,
    }[provider];
    setLoading(true);

    try {
      // Save plan to localStorage for retrieval after OAuth redirect
      if (validPlan) {
        localStorage.setItem('pendingPlan', validPlan);
      }
      const callbackPlan = validPlan ? `?plan=${validPlan}` : '';
      await authClient.signIn.social({
        provider,
        callbackURL: window.location.origin + '/' + callbackPlan,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to sign in with ${providerNames[provider]}`);
      setLoading(false);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLinkedProviders(null);
    setIsLoading(true);

    try {
      const result = await authClient.signIn.email({ email, password });

      if (result.error) {
        // Check if this email has OAuth providers linked
        try {
          const res = await fetch(`${API_BASE_URL}/api/user/linked-providers`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          const data = (await res.json()) as { providers: string[] };
          if (data.providers?.length > 0) {
            setLinkedProviders(data.providers);
            setError(null);
          } else {
            setError(result.error.message || 'Failed to sign in');
          }
        } catch {
          setError(result.error.message || 'Failed to sign in');
        }
      } else {
        navigate(validPlan ? `/?plan=${validPlan}` : '/');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await authClient.signUp.email({ name, email, password });

      if (result.error) {
        setError(result.error.message || 'Failed to sign up');
      } else if (result.data?.user && !result.data.user.emailVerified) {
        // Account created but email not verified yet
        setMode('check-email');
      } else {
        // Auto-signed in (no verification required)
        navigate(validPlan ? `/?plan=${validPlan}` : '/');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign up');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);

    try {
      const result = await authClient.requestPasswordReset({
        email,
        redirectTo: window.location.origin + '/reset-password',
      });

      if (result.error) {
        setError(result.error.message || 'Failed to send reset email');
      } else {
        setSuccessMessage('Check your email for a reset link.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);

    try {
      const result = await authClient.sendVerificationEmail({
        email,
        callbackURL: window.location.origin + '/',
      });

      if (result.error) {
        setError(result.error.message || 'Failed to resend verification email');
      } else {
        setSuccessMessage('Verification email sent!');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend verification email');
    } finally {
      setIsLoading(false);
    }
  };

  // OAuth buttons (shared between sign in and sign up)
  const oauthButtons = (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => handleSocialSignIn('google')}
        disabled={anyLoading}
        className="relative flex w-full items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isGoogleLoading ? <SpinnerIcon className="h-5 w-5" /> : <GoogleIcon className="h-5 w-5" />}
        Continue with Google
        {lastMethod === 'google' && <LastUsedBadge />}
      </button>

      <button
        type="button"
        onClick={() => handleSocialSignIn('github')}
        disabled={anyLoading}
        className="relative flex w-full items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isGitHubLoading ? <SpinnerIcon className="h-5 w-5" /> : <GitHubIcon className="h-5 w-5" />}
        Continue with GitHub
        {lastMethod === 'github' && <LastUsedBadge />}
      </button>

      <button
        type="button"
        onClick={() => handleSocialSignIn('microsoft')}
        disabled={anyLoading}
        className="relative flex w-full items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isMicrosoftLoading ? <SpinnerIcon className="h-5 w-5" /> : <MicrosoftIcon className="h-5 w-5" />}
        Continue with Microsoft
        {lastMethod === 'microsoft' && <LastUsedBadge />}
      </button>
    </div>
  );

  const divider = (
    <div className="my-6 flex items-center">
      <div className="flex-1 border-t border-gray-200" />
      <span className="px-4 text-sm text-gray-500">or</span>
      <div className="flex-1 border-t border-gray-200" />
    </div>
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="rounded-2xl bg-white px-8 py-10 shadow-xl">
          {/* Logo */}
          <div className="mb-8 flex flex-col items-center">
            <LogoIcon className="h-10 w-10" />
            <span className="mt-2 text-xl font-semibold tracking-tight text-gray-900">FlowRouters</span>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              {successMessage}
            </div>
          )}

          {/* OAuth Provider Hint */}
          {linkedProviders && linkedProviders.length > 0 && (
            <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              This account uses{' '}
              <span className="font-medium">
                {linkedProviders.map((p) => providerNames[p] || p).join(' and ')}
              </span>{' '}
              sign-in. Please use the {linkedProviders.length === 1 ? 'button' : 'buttons'} above.
            </div>
          )}

          {/* === SIGN IN MODE === */}
          {mode === 'signin' && (
            <>
              <h1 className="mb-6 text-center text-2xl font-bold text-gray-900">Log in to FlowRouters</h1>

              {oauthButtons}
              {divider}

              <form onSubmit={handleEmailSignIn} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                    <button
                      type="button"
                      onClick={() => { setError(null); setSuccessMessage(null); setMode('forgot-password'); }}
                      className="text-sm font-medium text-primary-600 hover:text-primary-700"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    placeholder="Enter your password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={anyLoading}
                  className="relative flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isLoading && <SpinnerIcon className="h-4 w-4" />}
                  Sign in
                  {lastMethod === 'email' && <LastUsedBadge />}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-gray-600">
                Don't have an account?{' '}
                <button onClick={() => switchMode('signup')} className="font-medium text-primary-600 hover:text-primary-700">
                  Sign up
                </button>
              </p>
            </>
          )}

          {/* === SIGN UP MODE === */}
          {mode === 'signup' && (
            <>
              <h1 className="mb-6 text-center text-2xl font-bold text-gray-900">Create your account</h1>

              {oauthButtons}
              {divider}

              <form onSubmit={handleEmailSignUp} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    autoComplete="name"
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    id="signup-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700">Password</label>
                  <input
                    id="signup-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    placeholder="At least 8 characters"
                  />
                </div>

                <button
                  type="submit"
                  disabled={anyLoading}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isLoading && <SpinnerIcon className="h-4 w-4" />}
                  Create account
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-gray-600">
                Already have an account?{' '}
                <button onClick={() => switchMode('signin')} className="font-medium text-primary-600 hover:text-primary-700">
                  Log in
                </button>
              </p>
            </>
          )}

          {/* === FORGOT PASSWORD MODE === */}
          {mode === 'forgot-password' && (
            <>
              <h1 className="mb-2 text-center text-2xl font-bold text-gray-900">Reset your password</h1>
              <p className="mb-6 text-center text-sm text-gray-600">
                Enter your email and we'll send you a reset link.
              </p>

              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    id="reset-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    placeholder="you@example.com"
                  />
                </div>

                <button
                  type="submit"
                  disabled={anyLoading}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isLoading && <SpinnerIcon className="h-4 w-4" />}
                  Send reset link
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-gray-600">
                <button onClick={() => switchMode('signin')} className="font-medium text-primary-600 hover:text-primary-700">
                  Back to sign in
                </button>
              </p>
            </>
          )}

          {/* === CHECK EMAIL MODE (post-signup verification) === */}
          {mode === 'check-email' && (
            <>
              <div className="mb-4 flex justify-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-50">
                  <svg className="h-7 w-7 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                </div>
              </div>
              <h1 className="mb-2 text-center text-2xl font-bold text-gray-900">Check your email</h1>
              <p className="mb-6 text-center text-sm text-gray-600">
                We sent a verification link to <span className="font-medium text-gray-900">{email}</span>
              </p>

              <button
                type="button"
                onClick={handleResendVerification}
                disabled={anyLoading}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading && <SpinnerIcon className="h-4 w-4" />}
                Resend verification email
              </button>

              <p className="mt-6 text-center text-sm text-gray-600">
                <button onClick={() => switchMode('signin')} className="font-medium text-primary-600 hover:text-primary-700">
                  Back to sign in
                </button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
